/* eslint-disable @next/next/no-img-element */
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { Button } from '@jove-frontend/uikit';
import { motion } from 'framer-motion';
import Balancer from 'react-wrap-balancer';
import BANNER_BG_MOBILE from '../assets/recruiter-page/banner-img-mobile.png';
import BANNER_BG from '../assets/recruiter-page/banner-img.png';
import VIDEO_OVERLAY from '../assets/recruiter-page/video-placeholder.jpg';
import BasicPage from '../components/Layouts/BasicPage';
import { LogoCloudRecruiter } from '../components/LogoCloud';
import { useHubspotModal } from '../components/Modals/HubspotModal';
import { FAQSection } from '../components/RecruiterAgency/FaqSection';
import { ProductsSection } from '../components/RecruiterAgency/ProductsSection';
import { USPSection } from '../components/RecruiterAgency/USPSection';
import { YoutubeEmbed } from '../components/YoutubeEmbed';
import { handleTracking } from '../util';

function BookDemoButton({ label }: { label?: string }) {
  return (
    <a
      href="https://calendly.com/d/cpdg-hyb-dpx/jove-demo"
      target="_blank"
      onClick={() => handleTracking()}
      className="mx-auto flex w-fit rounded-full border border-black/25 bg-white px-5 py-2 font-semibold transition-all duration-200 hover:scale-105 hover:border-black/50 hover:shadow-md md:mx-0"
    >
      {label ?? 'Book a demo'}
    </a>
  );
}

export const RecruitermentAgenciesPage = () => {
  const router = useRouter();
  const [showVideo, setShowVideo] = useState(false);
  const { openForm } = router.query;
  const { HubspotModal, setShowHubspotModal } = useHubspotModal({
    props: {
      formId: '692b0522-16d1-44b5-acb9-e4d26e1612d2',
      title: '',
      description: <></>,
    },
  });

  useEffect(() => {
    if (openForm) {
      setShowHubspotModal(true);
    }
  }, [openForm]);

  return (
    <>
      <Head>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Head>
      <BasicPage className="content-page">
        <div className="container relative mt-5 grid grid-cols-1 px-5 py-6 md:py-12 lg:grid-cols-2">
          <div className="space-y-4 py-8 lg:pr-16">
            <motion.h1
              className="text-center font-extrabold xl:text-left"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Balancer ratio={0.65}>Embedded insurance for staffing agencies, done right.</Balancer>
            </motion.h1>

            <motion.p
              className="text-center text-xl lg:text-left lg:text-2xl"
              initial={{ opacity: 0, scale: 0.2 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Stay compliant, and automate agency admin.
            </motion.p>

            <div className="flex w-full items-center justify-center space-x-4 lg:justify-start">
              <motion.div initial={{ opacity: 0, scale: 0.3 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.4 }}>
                <BookDemoButton />
              </motion.div>

              <motion.div initial={{ opacity: 0, scale: 0.3 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.4 }}>
                <Button onClick={() => setShowHubspotModal(true)} className="mx-auto hover:scale-105 hover:shadow-md md:mx-0">
                  Sign up
                </Button>
              </motion.div>
            </div>
          </div>

          {/* YouTube embed */}
          {showVideo ? (
            <div className="relative">
              <div className="w-full">
                <div className="z-1 relative">
                  <YoutubeEmbed embedId="Zz49aidejFY" />
                </div>
              </div>
            </div>
          ) : (
            <motion.div
              className="h-full w-full cursor-pointer object-cover transition-all duration-300 hover:scale-105"
              onClick={() => setShowVideo(true)}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
            >
              <Image src={VIDEO_OVERLAY} alt="Video overlay" />
            </motion.div>
          )}
        </div>

        <LogoCloudRecruiter />

        <USPSection />

        <ProductsSection />

        <FAQSection />

        <div className="px-4 lg:px-0">
          <motion.div
            className="bg-gray-dark container mx-auto mb-24 rounded-xl lg:flex lg:flex-row"
            initial={{ opacity: 0, y: -150, scale: 0.5 }}
            whileInView={{
              opacity: 1,
              scale: 1,
              y: 0,
              transition: { duration: 0.5, delay: 0.3 },
            }}
            viewport={{ once: true }}
          >
            <div className="mx-auto space-y-5 p-14 text-center lg:text-left">
              <h4 className="text-2xl font-bold text-white lg:text-[45px] lg:leading-[45px]">Bespoke insurance cover for modern agencies.</h4>
              <p className="text-white lg:text-lg">
                Speak to our bespoke brokerage team about your own agencies' corporate policy including international coverage, IR35 related and
                mis-classification protection
              </p>

              <motion.div
                initial={{ opacity: 0, x: -150, scale: 0.5 }}
                whileInView={{
                  opacity: 1,
                  scale: 1,
                  x: 0,
                  transition: { duration: 0.5, delay: 0.5 },
                }}
                viewport={{ once: true }}
              >
                <BookDemoButton label="Book a call" />
              </motion.div>
            </div>

            <div className="lg:flex-shrink-0">
              <div className="hidden lg:block">
                <Image src={BANNER_BG} alt="Insurance" />
              </div>
              <div className="mx-auto text-center lg:hidden">
                <Image src={BANNER_BG_MOBILE} alt="Insurance" />
              </div>
            </div>
          </motion.div>
        </div>

        <HubspotModal />
      </BasicPage>
    </>
  );
};

export default RecruitermentAgenciesPage;
